<script setup lang="ts">
import { useScrapingProgressStore } from "@/stores/scrapingProgress";
import { useJobQueueProgressStore } from "@/stores/jobQueueProgress";
import { computed } from "vue";

const progressStore = useScrapingProgressStore();
const queueProgressStore = useJobQueueProgressStore();

const statusIcon = computed(() => {
  // Prioritize job:progress status icon if available
  if (queueProgressStore.currentJob && queueProgressStore.statusIcon) {
    return queueProgressStore.statusIcon;
  }

  if (!progressStore.currentMessage) return "🔵";
  
  switch (progressStore.currentMessage.type) {
    case "job_started":
      return "🚀";
    case "navigating":
      return "🌐";
    case "page_loaded":
      return "✅";
    case "extracting":
      return "⚙️";
    case "job_complete":
      return "✅";
    case "job_failed":
      return "❌";
    default:
      return "🔵";
  }
});

const statusColor = computed(() => {
  // Prioritize job:progress status color if available
  if (queueProgressStore.currentJob) {
    switch (queueProgressStore.currentJob.subState) {
      case "started":
        return "text-blue-600";
      case "navigating":
        return "text-yellow-600";
      case "page_loaded":
        return "text-green-600";
      case "extracting":
        return "text-purple-600";
      case "completed":
        return "text-green-600";
      case "failed":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  }

  if (!progressStore.currentMessage) return "text-gray-600";
  
  switch (progressStore.currentMessage.type) {
    case "job_started":
      return "text-blue-600";
    case "navigating":
      return "text-yellow-600";
    case "page_loaded":
      return "text-green-600";
    case "extracting":
      return "text-purple-600";
    case "job_complete":
      return "text-green-600";
    case "job_failed":
      return "text-red-600";
    default:
      return "text-gray-600";
  }
});

// Display message: PRIORITIZE queue display (new system) over scraping progress (old system)
// Only show scraping progress if NO queue job is active
const displayMessage = computed(() => {
  if (queueProgressStore.queueDisplay) {
    // New system: Always use queue display if available
    return queueProgressStore.queueDisplay;
  }
  
  // Fallback to old scraping progress system
  return progressStore.currentMessage?.message || "";
});

// Check if either store has active content
const isActive = computed(() => {
  return queueProgressStore.currentJob || progressStore.isActive;
});
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 -translate-y-2"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 -translate-y-2"
  >
    <div
      v-if="isActive"
      class="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-md border border-gray-200"
    >
      <!-- Status Icon -->
      <span class="text-xl">{{ statusIcon }}</span>
      
      <!-- Message -->
      <div class="flex flex-col min-w-0">
        <span
          :class="['text-sm font-medium truncate', statusColor]"
        >
          {{ displayMessage }}
        </span>
        
        <!-- Details from scraping progress (if available) -->
        <span
          v-if="progressStore.currentMessage?.details"
          class="text-xs text-gray-500 truncate"
        >
          <template v-if="progressStore.currentMessage.details.currentRound && progressStore.currentMessage.details.totalRounds">
            Runde {{ progressStore.currentMessage.details.currentRound }}/{{ progressStore.currentMessage.details.totalRounds }}
          </template>
          <template v-if="progressStore.currentMessage.details.brand || progressStore.currentMessage.details.model || progressStore.currentMessage.details.fin">
            ·
            <span v-if="progressStore.currentMessage.details.brand" class="font-semibold text-gray-700">
              {{ progressStore.currentMessage.details.brand }}
            </span>
            <span v-if="progressStore.currentMessage.details.brand && progressStore.currentMessage.details.model" class="font-semibold text-gray-700">
              {{" "}}
            </span>
            <span v-if="progressStore.currentMessage.details.model" class="font-semibold text-gray-700">
              {{ progressStore.currentMessage.details.model }}
            </span>
            <span v-if="progressStore.currentMessage.details.fin" class="font-mono text-gray-600">
              (FIN: {{ progressStore.currentMessage.details.fin }})
            </span>
          </template>
          <template v-if="progressStore.currentMessage.details.vehicleTitle">
            · {{ progressStore.currentMessage.details.vehicleTitle }}
          </template>
          <template v-if="progressStore.currentMessage.details.resultsFound !== undefined">
            · {{ progressStore.currentMessage.details.resultsFound }} Ergebnisse
          </template>
          <template v-if="progressStore.currentMessage.details.attemptNumber && progressStore.currentMessage.details.maxAttempts">
            · Versuch {{ progressStore.currentMessage.details.attemptNumber }}/{{ progressStore.currentMessage.details.maxAttempts }}
          </template>
        </span>
      </div>
      
      <!-- Close Button -->
      <button
        @click="() => {
          progressStore.hide();
          queueProgressStore.clear();
        }"
        class="ml-auto p-1 hover:bg-gray-100 rounded transition-colors"
        title="Ausblenden"
      >
        <svg
          class="w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  </Transition>
</template>

